/**
 * Test Router Panel
 *
 * Pannello per configurare il Test Router che permette di usare UN telefono fisico
 * per testare messaggi WhatsApp come se arrivassero da numeri diversi (clienti simulati).
 *
 * Flusso:
 * 1. Configuri il tuo numero di telefono reale
 * 2. Scegli quale cliente simulare (dropdown o numero libero)
 * 3. Attivi il Test Mode
 * 4. Invii messaggi dal tuo telefono → il bot li processa come se venissero dal cliente simulato
 * 5. Le risposte arrivano al TUO telefono (non al cliente reale)
 */

import { useState, useEffect } from 'react';
import { Phone, Users, Power, PowerOff, RefreshCw, AlertTriangle, CheckCircle, HardDrive } from 'lucide-react';
import { useWhatsAppConfig } from '../WhatsAppProvider';
import { waAuthHeaders } from '../waFetch';

interface TestRouterConfig {
  test_mode_enabled: boolean;
  test_mode_persistent: boolean;
  test_real_phone: string | null;
  test_simulated_phone: string | null;
}

interface Customer {
  id: number;
  company_name: string | null;  // ragione sociale
  first_name: string | null;    // nome
  last_name: string | null;     // cognome
  phone: string;                // numero telefono completo
  phone_whatsapp: number;       // 1 = abilitato, 0 = disabilitato
  referente1_name?: string;
  referente1_phone?: string;
  referente1_whatsapp?: number;
}

export function TestSimulatorPanel() {
  const { apiBaseUrl } = useWhatsAppConfig();

  const [config, setConfig] = useState<TestRouterConfig>({
    test_mode_enabled: false,
    test_mode_persistent: true,
    test_real_phone: null,
    test_simulated_phone: null
  });
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Input mode: 'dropdown' o 'custom'
  const [inputMode, setInputMode] = useState<'dropdown' | 'custom'>('dropdown');
  const [customPhone, setCustomPhone] = useState('');

  // Load config e devices
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Load config
      const configRes = await fetch(`${apiBaseUrl}/test/router-config`, { headers: waAuthHeaders() });
      const configData = await configRes.json();
      if (configData.success) {
        setConfig(configData.data);
        // Se c'è un numero simulato che non è nel dropdown, passa a custom mode
        if (configData.data.test_simulated_phone) {
          setCustomPhone(configData.data.test_simulated_phone);
        }
      }

      // Load customers per dropdown (dall'anagrafica clienti principale)
      const customersRes = await fetch(`${apiBaseUrl}/customers`, { headers: waAuthHeaders() });
      const customersData = await customersRes.json();
      if (customersData.success) {
        setCustomers(customersData.customers || []);
      }
    } catch (err) {
      setError(`Errore caricamento: ${err instanceof Error ? err.message : 'Sconosciuto'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const saveConfig = async (newConfig: Partial<TestRouterConfig>) => {
    setIsSaving(true);
    setError(null);
    setSuccess(null);

    const updatedConfig = { ...config, ...newConfig };

    try {
      const res = await fetch(`${apiBaseUrl}/test/router-config`, {
        method: 'PUT',
        headers: waAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(updatedConfig)
      });

      const data = await res.json();
      if (data.success) {
        setConfig(updatedConfig);
        setSuccess(updatedConfig.test_mode_enabled ? 'Test Router ATTIVATO' : 'Test Router disattivato');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || 'Errore salvataggio');
      }
    } catch (err) {
      setError(`Errore: ${err instanceof Error ? err.message : 'Sconosciuto'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleTestMode = () => {
    // Validazione
    if (!config.test_mode_enabled) {
      // Stiamo attivando
      if (!config.test_real_phone) {
        setError('Inserisci il tuo numero di telefono di test');
        return;
      }
      const simPhone = inputMode === 'custom' ? customPhone : config.test_simulated_phone;
      if (!simPhone) {
        setError('Seleziona o inserisci un numero da simulare');
        return;
      }
      saveConfig({
        test_mode_enabled: true,
        test_simulated_phone: simPhone
      });
    } else {
      // Stiamo disattivando
      saveConfig({ test_mode_enabled: false });
    }
  };

  const handleReset = async () => {
    setIsSaving(true);
    try {
      const res = await fetch(`${apiBaseUrl}/test/router-reset`, { method: 'POST', headers: waAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setConfig(prev => ({ ...prev, test_mode_enabled: false }));
        setSuccess('Test Router disattivato');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError('Errore reset');
    } finally {
      setIsSaving(false);
    }
  };

  // Tutti i clienti con numero telefono valido
  const availableCustomers = customers.filter(c => c.phone);

  if (isLoading) {
    return (
      <div className="bg-slate-800 rounded-lg border border-purple-700/30 p-4">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 text-purple-400 animate-spin" />
          <span className="ml-2 text-slate-400">Caricamento...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 rounded-lg border border-purple-700/30 p-4">
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-purple-300 flex items-center gap-2">
          <span className="text-2xl">🔀</span>
          Test Router - Simula Numeri Clienti
        </h3>
        <p className="text-xs text-slate-400 mt-1">
          Usa il tuo telefono reale per testare come se i messaggi arrivassero da clienti diversi.
        </p>
      </div>

      {/* Status Banner */}
      <div className={`mb-4 p-3 rounded-lg border ${
        config.test_mode_enabled
          ? 'bg-green-900/30 border-green-600'
          : 'bg-slate-700/50 border-slate-600'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {config.test_mode_enabled ? (
              <Power className="w-5 h-5 text-green-400" />
            ) : (
              <PowerOff className="w-5 h-5 text-slate-400" />
            )}
            <span className={`font-semibold ${config.test_mode_enabled ? 'text-green-400' : 'text-slate-400'}`}>
              {config.test_mode_enabled ? '✅ TEST ROUTER ATTIVO' : 'Test Router disattivato'}
            </span>
          </div>
          {config.test_mode_enabled && (
            <button
              onClick={handleReset}
              disabled={isSaving}
              className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs transition disabled:opacity-50"
            >
              Disattiva
            </button>
          )}
        </div>
        {config.test_mode_enabled && config.test_real_phone && config.test_simulated_phone && (
          <div className="mt-2 text-sm text-slate-300">
            <div>📱 <strong>Tuo telefono:</strong> {config.test_real_phone}</div>
            <div>👤 <strong>Simulato come:</strong> {config.test_simulated_phone}</div>
          </div>
        )}
      </div>

      {/* Configuration */}
      <div className="space-y-4">
        {/* My Phone Number */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1">
            <Phone className="w-4 h-4 inline mr-1" />
            Il mio telefono di test
          </label>
          <input
            type="tel"
            value={config.test_real_phone || ''}
            onChange={(e) => setConfig(prev => ({ ...prev, test_real_phone: e.target.value }))}
            placeholder="+393381234567"
            disabled={config.test_mode_enabled}
            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white placeholder-slate-500 text-sm focus:outline-none focus:border-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <p className="text-xs text-slate-500 mt-1">
            Il numero del tuo telefono fisico da cui invierai i messaggi di test
          </p>
        </div>

        {/* Simulated Number Selection Mode */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            <Users className="w-4 h-4 inline mr-1" />
            Simula messaggi dal cliente:
          </label>

          {/* Toggle dropdown/custom */}
          <div className="flex gap-2 mb-2">
            <button
              onClick={() => setInputMode('dropdown')}
              disabled={config.test_mode_enabled}
              className={`flex-1 px-3 py-2 rounded text-sm font-medium transition ${
                inputMode === 'dropdown'
                  ? 'bg-purple-600 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              📋 Scegli cliente
            </button>
            <button
              onClick={() => setInputMode('custom')}
              disabled={config.test_mode_enabled}
              className={`flex-1 px-3 py-2 rounded text-sm font-medium transition ${
                inputMode === 'custom'
                  ? 'bg-purple-600 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              ✏️ Numero libero
            </button>
          </div>

          {inputMode === 'dropdown' ? (
            <select
              value={config.test_simulated_phone || ''}
              onChange={(e) => setConfig(prev => ({ ...prev, test_simulated_phone: e.target.value }))}
              disabled={config.test_mode_enabled}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">-- Seleziona cliente ({availableCustomers.length}) --</option>
              {availableCustomers.map((customer) => {
                // Formatta nome completo
                const parts: string[] = [];
                if (customer.company_name) parts.push(customer.company_name);
                if (customer.first_name || customer.last_name) {
                  const fullName = [customer.first_name, customer.last_name].filter(Boolean).join(' ');
                  if (fullName) parts.push(fullName);
                }
                const displayName = parts.join(' - ') || 'Cliente senza nome';
                const waStatus = customer.phone_whatsapp === 1 ? '✅' : '❌';

                return (
                  <option key={customer.id} value={customer.phone}>
                    {waStatus} {displayName} | {customer.phone}
                  </option>
                );
              })}
            </select>
          ) : (
            <input
              type="tel"
              value={customPhone}
              onChange={(e) => {
                setCustomPhone(e.target.value);
                setConfig(prev => ({ ...prev, test_simulated_phone: e.target.value }));
              }}
              placeholder="+393358054240"
              disabled={config.test_mode_enabled}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white placeholder-slate-500 text-sm focus:outline-none focus:border-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          )}
          <p className="text-xs text-slate-500 mt-1">
            {inputMode === 'dropdown'
              ? 'Scegli un cliente esistente per testare le sue turbine'
              : 'Inserisci un numero qualsiasi (anche non registrato per testare rifiuto)'
            }
          </p>
        </div>

        {/* Persistence Toggle */}
        <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded">
          <div className="flex items-center gap-2">
            <HardDrive className="w-4 h-4 text-slate-400" />
            <div>
              <span className="text-sm text-slate-300">Avvia al Boot</span>
              <p className="text-xs text-slate-500">Se attivo, il Test Router si avvia automaticamente al riavvio del bot</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.test_mode_persistent}
              onChange={(e) => {
                const newValue = e.target.checked;
                setConfig(prev => ({ ...prev, test_mode_persistent: newValue }));
                saveConfig({ test_mode_persistent: newValue });
              }}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
          </label>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="p-3 bg-red-900/30 border border-red-700 rounded flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <span className="text-sm text-red-300">{error}</span>
          </div>
        )}
        {success && (
          <div className="p-3 bg-green-900/30 border border-green-700 rounded flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
            <span className="text-sm text-green-300">{success}</span>
          </div>
        )}

        {/* Activate Button */}
        <button
          onClick={handleToggleTestMode}
          disabled={isSaving}
          className={`w-full px-4 py-3 rounded font-medium transition flex items-center justify-center gap-2 ${
            config.test_mode_enabled
              ? 'bg-red-600 hover:bg-red-700 text-white'
              : 'bg-purple-600 hover:bg-purple-700 text-white'
          } disabled:opacity-50`}
        >
          {isSaving ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              Salvataggio...
            </>
          ) : config.test_mode_enabled ? (
            <>
              <PowerOff className="w-5 h-5" />
              Disattiva Test Router
            </>
          ) : (
            <>
              <Power className="w-5 h-5" />
              Attiva Test Router
            </>
          )}
        </button>
      </div>

      {/* Info Box */}
      <div className="mt-4 p-3 bg-slate-900/50 border border-slate-700 rounded text-xs text-slate-400">
        <strong className="text-slate-300">Come funziona:</strong>
        <ol className="mt-1 space-y-1 list-decimal list-inside">
          <li>Inserisci il numero del tuo telefono fisico</li>
          <li>Scegli quale cliente simulare (o inserisci un numero qualsiasi)</li>
          <li>Attiva il Test Router</li>
          <li>Invia messaggi dal tuo telefono → il bot li elabora come se venissero dal cliente</li>
          <li>Le risposte arrivano a TE, non al cliente reale!</li>
        </ol>
      </div>

      {/* Warning */}
      {config.test_mode_enabled && (
        <div className="mt-3 p-3 bg-yellow-900/30 border border-yellow-700 rounded flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-yellow-200">
            <strong>ATTENZIONE:</strong> Test Router attivo! Tutti i messaggi dal tuo telefono
            saranno processati come se venissero da <strong>{config.test_simulated_phone}</strong>.
            Ricordati di disattivare quando hai finito i test.
          </div>
        </div>
      )}
    </div>
  );
}
